// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  h,
  Prop,
  Watch,
} from '@stencil/core'

import type { Size } from '../shared'

export type LoaderSize = Size | 'jumbo'
export type LoaderStatus = 'error' | 'success' | 'warning'

/**
 * The `<qds-loader>` element displays an indicator showing that content is
 * being loaded or processed.
 *
 * @see https://quartz.se.com/build/components/loader
 */
@Component({
  tag: 'qds-loader',
  formAssociated: true,
  shadow: true,
  styleUrl: 'loader.css',
})
export class Loader implements ComponentInterface {
  /**
   * The loader's size.
   */
  @Prop() public readonly size?: LoaderSize = 'standard'

  /**
   * The loader's status.
   */
  @Prop() public readonly status?: LoaderStatus

  @Element() private readonly host!: HTMLQdsLoaderElement

  @AttachInternals() private readonly internals!: ElementInternals

  get #computedSize(): LoaderSize {
    switch (this.size) {
      case 'small':
      case 'large':
      case 'jumbo': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedStatus(): LoaderStatus | 'loading' {
    switch (this.status) {
      case 'error':
      case 'success':
      case 'warning': {
        return this.status
      }
      default: {
        return 'loading'
      }
    }
  }

  @Watch('status')
  protected statusChanged(): void {
    this.internals.ariaValueNow =
      this.#computedStatus === 'loading'
        ? // eslint-disable-next-line unicorn/no-null
          null
        : this.internals.ariaValueMax
  }

  public componentWillLoad(): void {
    this.internals.role = 'progressbar'
    this.internals.ariaValueMin = this.host.ariaValueMin ?? '0'
    this.internals.ariaValueMax = this.host.ariaValueMax ?? '1'
    this.#defineGetter('labels', () => this.internals.labels)

    this.statusChanged()
  }

  public render() {
    return this.#computedStatus === 'loading' ? (
      <div
        aria-hidden="true"
        class="qds-loader"
        data-size={this.#computedSize}
        data-status={this.#computedStatus}
      />
    ) : (
      <qds-icon
        aria-hidden="true"
        class="qds-loader"
        data-size={this.#computedSize}
        data-status={this.#computedStatus}
        library="core"
        name={`status-${this.status}`}
      />
    )
  }

  #defineGetter(p: PropertyKey, get: () => unknown): void {
    Object.defineProperty(this.host, p, { enumerable: true, get })
  }
}
